import { useEffect, useState, useCallback, useRef } from "react";
import * as global from '../data/global';
import { MuiMaterials } from '../@index.js';
import { createApiEndpoint } from '../index.js';
import { useAuth } from "../provider/authProvider.js";
import {
    confirmDialogBackdropSx,
    confirmDialogPaperSx,
    confirmDialogTitleSx,
    confirmDialogActionsSx,
    confirmDialogButtonSx,
    outlinedActionButtonSx,
} from "./windowChrome/index.js";


/** Fast polling while scan window watches scan status SP + getLastScan. */

const SCAN_STATUS_POLL_MS = 400;



function parseScanJsonCommand(jsonCommand) {

    if (jsonCommand == null || jsonCommand === "") {

        return [];

    }

    try {

        const cmd = typeof jsonCommand === "string" ? JSON.parse(jsonCommand) : jsonCommand;

        if (!Array.isArray(cmd)) {

            return [];

        }

        return cmd.map((id) => Number(id)).filter((id) => Number.isFinite(id));

    } catch {

        return [];

    }

}



function sameCableIdSet(a, b) {

    if (!a?.length || !b?.length || a.length !== b.length) {

        return false;

    }

    const sa = [...a].sort((x, y) => x - y);

    const sb = [...b].sort((x, y) => x - y);

    return sa.every((v, i) => v === sb[i]);

}



function parseFinishedCableIds(finishedCables) {

    if (finishedCables == null || finishedCables === "") {

        return [];

    }

    return String(finishedCables)

        .split(",")

        .map((s) => Number(s.trim()))

        .filter((id) => Number.isFinite(id));

}



function readStatusBool(row, ...keys) {

    if (!row) return false;

    for (const k of keys) {

        const v = row[k];

        if (v === true || v === 1 || v === "1" || v === "true") return true;

    }

    return false;

}



function readStatusInt(row, ...keys) {

    if (!row) return NaN;

    for (const k of keys) {

        const v = row[k];

        if (v == null || v === "") continue;

        const n = Number(v);

        if (Number.isFinite(n)) return n;

    }

    return NaN;

}



/** Normalize CheckSiteDirectScanStatus SP payload (DataTable JSON). */
function parseDirectScanStatusRows(payload) {
    if (payload == null || payload === false) return [];

    let data = payload;
    if (typeof data === "string") {
        try {
            data = JSON.parse(data);
        } catch {
            return [];
        }
    }
    if (data == null || typeof data !== "object") return [];

    // Newtonsoft column-major DataTable JSON: { CableId: [178, 179], IsHeltec: [1, 1], ... }
    const cableIdCol = data.CableId ?? data.cableId;
    if (!Array.isArray(data) && cableIdCol && Array.isArray(cableIdCol)) {
        const lastGoodCol = data.LastGoodTempCheck ?? data.lastGoodTempCheck ?? [];
        const heltecCol = data.IsHeltec ?? data.isHeltec ?? [];
        const finishedCol = data.IsFinishedScanning ?? data.isFinishedScanning ?? [];
        return cableIdCol.map((id, i) => ({
            cableId: Number(id),
            lastGoodTempCheck: lastGoodCol[i] ?? null,
            isHeltec: readStatusBool({ v: heltecCol[i] }, "v"),
            isFinishedScanning: readStatusBool({ v: finishedCol[i] }, "v"),
        })).filter((r) => Number.isFinite(r.cableId));
    }

    let rows = data;
    if (!Array.isArray(rows)) {
        rows = data.Table ?? data.table ?? data.Rows ?? data.rows ?? [];
    }
    if (!Array.isArray(rows) && rows && typeof rows === "object") {
        const values = Object.values(rows);
        if (values.length && values.every((v) => v && typeof v === "object" && !Array.isArray(v))) {
            rows = values;
        }
    }
    if (!Array.isArray(rows)) return [];

    return rows.map((row) => ({
        cableId: readStatusInt(row, "CableId", "cableId"),
        lastGoodTempCheck: row.LastGoodTempCheck ?? row.lastGoodTempCheck ?? null,
        isHeltec: readStatusBool(row, "IsHeltec", "isHeltec"),
        isFinishedScanning: readStatusBool(row, "IsFinishedScanning", "isFinishedScanning"),
    })).filter((r) => Number.isFinite(r.cableId));
}

function isScanWindowVisible(scanWindow) {
    if (!scanWindow) return false;
    if (scanWindow.style.display === "none") return false;
    try {
        return window.getComputedStyle(scanWindow).display !== "none";
    } catch {
        return scanWindow.style.display !== "none";
    }
}

function hasPendingCablesFromStatus(activeCableIds, statusRows) {
    if (!statusRows?.length || !activeCableIds?.length) return false;
    const activeSet = new Set(
        activeCableIds.filter((id) => Number.isFinite(Number(id))).map((id) => Number(id))
    );
    if (activeSet.size === 0) return false;
    return statusRows.some((r) => activeSet.has(r.cableId) && !r.isFinishedScanning);
}

function isScanFlagSet(value) {
    return value === true || value === 1 || value === "1" || value === "true";
}

/**
 * True only while the DirectScan row is open.
 * Same rule as Sites Scanning: not complete and not canceled.
 * A closed row can still list cables that never landed in finishedCables
 * (Heltec leftovers, a force-close). Those must not keep Cancel showing.
 */
function isServerDirectScanActive(last) {
    if (!last || isScanFlagSet(last.isCanceled)) {
        return false;
    }
    return !isScanFlagSet(last.isComplete);
}

function listHasCableId(ids, cableId) {
    const n = Number(cableId);
    if (!Number.isFinite(n) || !ids?.length) return false;
    return ids.some((id) => Number(id) === n);
}

/** True only for an in-flight direct scan (not historical complete/canceled rows). */
function isDirectScanInProgress(last, activeCableIds) {
    if (global.gw.selectedSite?.addScanInFlight === true) return true;
    if (!last || isScanFlagSet(last.isCanceled)) return false;
    if (isScanFlagSet(last.isComplete)) return false;
    return true;
}

function areAllScanCablesFinished(cableIds, statusRows, last) {
    if (!cableIds?.length) return true;
    const statusById = new Map((statusRows || []).map((r) => [Number(r.cableId), r]));
    const finishedSet = new Set(parseFinishedCableIds(last?.finishedCables));
    return cableIds.every((id) => {
        const n = Number(id);
        if (finishedSet.has(n)) return true;
        const row = statusById.get(n);
        return row?.isFinishedScanning === true;
    });
}

function isActiveScanSessionPending(last, activeCableIds, statusRows) {
    if (global.gw.selectedSite?.addScanInFlight === true) return true;
    if (last && !isScanFlagSet(last.isCanceled) && !isScanFlagSet(last.isComplete)) {
        return true;
    }
    if (!Array.isArray(activeCableIds) || activeCableIds.length === 0) return false;
    const scope = activeCableIds.map(Number).filter((id) => Number.isFinite(id));
    if (!scope.length) return false;
    return !areAllScanCablesFinished(scope, statusRows, last);
}

function filterVisibleScanCableIds(cableIds, visibleCableIds) {
    if (!Array.isArray(visibleCableIds)) return cableIds;
    if (visibleCableIds.length === 0) return [];
    const visible = new Set(
        visibleCableIds.map((id) => Number(id)).filter((id) => Number.isFinite(id))
    );
    return cableIds.filter((id) => visible.has(Number(id)));
}

function resolveActiveScanCableIds(activeCableIds, last) {
    if (Array.isArray(activeCableIds) && activeCableIds.length > 0) {
        return activeCableIds;
    }
    if (isDirectScanInProgress(last, activeCableIds)) {
        return parseScanJsonCommand(last?.jsonCommand);
    }
    return [];
}



function ensureScanSensorRefreshState() {
    const site = global.gw.selectedSite;
    if (!site) return false;
    if (!site.appliedScanSensorCables) {
        site.appliedScanSensorCables = [];
    }
    if (!site.pendingScanSensorFetches) {
        site.pendingScanSensorFetches = {};
    }
    return true;
}

/** Refresh scan row temps when a cable finishes (Heltec uplink or local BTS). */
function refreshCableSnapshotIfReady(cableId, updateCablesFn, options = {}) {
    if (!updateCablesFn || !Number.isFinite(cableId)) return;
    if (!ensureScanSensorRefreshState()) return;

    const siteAtStart = global.gw.selectedSite;
    const siteGuidAtStart = siteAtStart?.siteGuid;
    const key = String(cableId);

    if (siteAtStart.appliedScanSensorCables.includes(key)) return;
    if (siteAtStart.pendingScanSensorFetches[key]) return;

    siteAtStart.pendingScanSensorFetches[key] = true;

    createApiEndpoint()
        .fetchCableSensorSnapshotForScan("direct", cableId)
        .then((gql) => {
            const site = global.gw.selectedSite;
            // Site switch replaces selectedSite — ignore stale snapshot responses.
            if (!site || site.siteGuid !== siteGuidAtStart) return;
            if (!ensureScanSensorRefreshState()) return;
            delete site.pendingScanSensorFetches[key];

            const snap = gql?.data?.getCableSensorSnapshotForScan;
            if (!snap?.cableId) return;

            const status = String(snap.status ?? "").toLowerCase();
            const hasTemps = Array.isArray(snap.sensorHistories) && snap.sensorHistories.length > 0;

            // Local BTS already wrote Sensor rows in updateCable; LGT vs DateStarted can stay Pending.
            const apply = (status === "ready" || (options.allowPending && hasTemps)) &&
                !site.appliedScanSensorCables.includes(key);

            if (apply) {
                site.appliedScanSensorCables.push(key);
                updateCablesFn(snap);
            }
        })
        .catch(() => {
            const site = global.gw.selectedSite;
            if (!site || site.siteGuid !== siteGuidAtStart) return;
            if (!site.pendingScanSensorFetches) return;
            delete site.pendingScanSensorFetches[key];
        });
}



/** Poll snapshot for finished cables until temps are Ready (SP can flip before BTS row catches up). */

function syncFinishedCableSnapshots(statusRows, updateCablesFn, scanCableIds, last) {

    if (!updateCablesFn) return;

    const scope = scanCableIds?.length

        ? new Set(scanCableIds.map((id) => Number(id)))

        : null;

    const finishedFromBts = new Set(parseFinishedCableIds(last?.finishedCables));

    const idsToRefresh = new Set(finishedFromBts);

    for (let i = 0; i < (statusRows || []).length; i++) {

        const row = statusRows[i];

        if (row?.isFinishedScanning && Number.isFinite(row.cableId)) {

            idsToRefresh.add(row.cableId);

        }

    }

    idsToRefresh.forEach((cableId) => {

        if (!Number.isFinite(cableId)) return;

        if (scope && !scope.has(cableId)) return;

        refreshCableSnapshotIfReady(cableId, updateCablesFn, {

            allowPending: finishedFromBts.has(cableId),

        });

    });

}



function resolveBlinkVisibleCableIds(activeCableIds, visibleCableIds, last) {
    if (last && !isScanFlagSet(last.isComplete) && !isScanFlagSet(last.isCanceled)
        && last.scanCable != null && last.scanCable !== "") {
        if (Array.isArray(activeCableIds) && activeCableIds.length > 0) {
            return activeCableIds;
        }
    }
    return visibleCableIds;
}

/** Which cable row to blink — local BTS scanCable first, then Heltec-unfinished fallback. */
function resolveBlinkCableId(last, activeCableIds, statusRows, visibleCableIds) {
    if (last && (isScanFlagSet(last.isComplete) || isScanFlagSet(last.isCanceled))) {
        if (!isDirectScanInProgress(last, activeCableIds)) {
            return undefined;
        }
    }

    const blinkVisibleIds = resolveBlinkVisibleCableIds(activeCableIds, visibleCableIds, last);
    const finishedSet = new Set(parseFinishedCableIds(last?.finishedCables));

    const visibleOk = (id) => {
        if (!Array.isArray(blinkVisibleIds) || blinkVisibleIds.length === 0) return true;
        return listHasCableId(blinkVisibleIds, id);
    };

    const activeCableIdsForUi = filterVisibleScanCableIds(
        resolveActiveScanCableIds(activeCableIds, last),
        blinkVisibleIds
    );

    const firstNotInFinishedSet = (ids) => {
        for (const id of ids) {
            const n = Number(id);
            if (Number.isFinite(n) && !finishedSet.has(n) && visibleOk(n)) {
                return n;
            }
        }
        return null;
    };

    // BTS reports the cable on the terminator via scanCable. Forced-open cables skip Modbus reads
    // so CheckSiteDirectScanStatus may never flip IsFinishedScanning even after finishedCables advances.
    if (last && !isScanFlagSet(last.isComplete) && !isScanFlagSet(last.isCanceled)
        && last.scanCable != null && last.scanCable !== "") {
        const scanCableId = Number(last.scanCable);
        if (Number.isFinite(scanCableId) && visibleOk(scanCableId)) {
            if (!finishedSet.has(scanCableId)) {
                return scanCableId;
            }
            const nextAfterFinished = firstNotInFinishedSet(activeCableIdsForUi);
            if (nextAfterFinished != null) {
                return nextAfterFinished;
            }
            return scanCableId;
        }
    }

    if (!activeCableIdsForUi.length) {
        return undefined;
    }

    const nextPending = firstNotInFinishedSet(activeCableIdsForUi);
    if (nextPending != null) {
        return nextPending;
    }

    if (areAllScanCablesFinished(activeCableIdsForUi, statusRows, last)) {
        return undefined;
    }

    const statusById = new Map((statusRows || []).map((r) => [Number(r.cableId), r]));
    const heltecIds = [];
    const legacyIds = [];
    const unknownIds = [];

    for (const id of activeCableIdsForUi) {
        const row = statusById.get(Number(id));
        if (row?.isHeltec) {
            heltecIds.push(id);
        } else if (row && !row.isHeltec) {
            legacyIds.push(id);
        } else {
            unknownIds.push(id);
        }
    }

    const firstUnfinished = (ids) => {
        for (const id of ids) {
            const n = Number(id);
            if (!Number.isFinite(n) || finishedSet.has(n)) {
                continue;
            }
            const row = statusById.get(n);
            if (!row || !row.isFinishedScanning) {
                return n;
            }
        }
        return null;
    };

    // Heltec MQTT phase (BTS has not reported a scanCable yet): Heltec cables first.
    const heltecPending = firstUnfinished(heltecIds);
    if (heltecPending != null) {
        return heltecPending;
    }

    if (heltecIds.length > 0) {
        const legacyPending = firstUnfinished(legacyIds);
        if (legacyPending != null) {
            return legacyPending;
        }
        const unknownPending = firstUnfinished(unknownIds);
        if (unknownPending != null) {
            return unknownPending;
        }
    }

    if (heltecIds.length === 0) {
        const legacyPending = firstUnfinished(legacyIds);
        if (legacyPending != null) {
            return legacyPending;
        }
    }

    return undefined;
}



function reportAddScanFailure(message) {
    if (global.gw?.test) {
        console.error(message);
    }
    window.alert(message);
}



function resetScanUiState() {
    const site = global.gw.selectedSite;
    if (!site) return;
    site.activeScanCableIds = null;
    site.appliedScanSensorCables = [];
    site.pendingScanSensorFetches = {};
    site.finishedCables = [];
    site.addScanInFlight = false;
}



const Scan = (props) => {

    const { setToken, token, clearToken } = useAuth();

    const [isScanning, setIsScanning] = useState(false);

    const [open, setOpen] = useState(false);

    const [forceUpdate, setForceUpdate] = useState(0);

    const selectedCablesRef = useRef(props.selectedCables);
    selectedCablesRef.current = props.selectedCables;



    const getThemeColor = useCallback((lightColor, darkColor) => {

        if (global.gw.theme === 'light') {

            return lightColor;

        }

        return darkColor;

    }, []);



    useEffect(() => {

        const handleThemeChange = () => {

            setForceUpdate(prev => prev + 1);

        };

        window.addEventListener('themeChanged', handleThemeChange);

        return () => window.removeEventListener('themeChanged', handleThemeChange);

    }, []);



    const handleOpen = () => {

        setOpen(true);
        scheduleNextPoll(0);

        let cableList = window.document.getElementsByClassName("cableList")[0];

        if (cableList && cableList.style.display === "unset") {

            cableList.style.display = "none";

            let expand = cableList.parentNode.children[0].children[0].children;

            if (expand) {

                expand[1].style.display = "inline-block";

                expand[2].style.display = "none";

            }

        }



        let binList = window.document.getElementsByClassName("binList")[0];

        if (binList && binList.style.display === "unset") {

            binList.style.display = "none";

            let expand = binList.parentNode.children[0].children[0].children;

            if (expand) {

                expand[1].style.display = "inline-block";

                expand[2].style.display = "none";

            }

        }

    };

    const handleClose = () => setOpen(false);

    const pollInFlightRef = useRef(false);
    const checkIfScanningRef = useRef(() => {});
    const lastKnownDirectScanRef = useRef(null);

    useEffect(() => {
        checkIfScanningRef.current();
        return () => {
            if (global.gw.selectedSite?.refreshId) {
                clearTimeout(global.gw.selectedSite.refreshId);
            }
        };
    }, []);

    const scheduleNextPoll = (delayMs) => {
        const site = global.gw.selectedSite;
        if (!site) return;
        clearTimeout(site.refreshId);
        site.refreshId = setTimeout(() => {
            checkIfScanningRef.current();
        }, delayMs);
    };



    const handleAuthRenew = (error) => {

        if (error?.message &&

            error.message.endsWith("401") &&

            global.gw.token &&

            global.gw.auth) {

            createApiEndpoint(global.Endpoints.GrainWave)

                .fetchRenew()

                .then(r => {

                    if (r && global.gw.token) {

                        setToken(global.gw.token);

                    } else {

                        clearToken();

                    }

                })

                .catch(() => {

                    clearToken();

                });

        }

    };



    const checkIfScanning = () => {
        const site = global.gw.selectedSite;
        const scanWindow = window.document.getElementsByClassName("scanWindow")[0];
        if (!scanWindow || !site) {
            if (site?.refreshId) clearTimeout(site.refreshId);
            return;
        }

        const pollSiteGuid = site.siteGuid;
        const activeCableIds = site.activeScanCableIds;
        const scanSessionActive = site.addScanInFlight === true ||
            isServerDirectScanActive(lastKnownDirectScanRef.current) ||
            (Array.isArray(activeCableIds) && activeCableIds.length > 0);
        const windowVisible = isScanWindowVisible(scanWindow);

        // Slow poll only when window hidden AND no active scan session
        if (!windowVisible && !scanSessionActive) {
            scheduleNextPoll(site.refreshRate);
            return;
        }

        if (pollInFlightRef.current) {
            scheduleNextPoll(SCAN_STATUS_POLL_MS);
            return;
        }
        pollInFlightRef.current = true;

        const lastScanPromise = createApiEndpoint().fetchLastScan('direct');
        const statusPromise = createApiEndpoint().fetchSiteDirectScanStatus('direct');

        Promise.all([lastScanPromise, statusPromise]).then(([gql, statusPayload]) => {
            pollInFlightRef.current = false;

            const liveSite = global.gw.selectedSite;
            if (!liveSite || liveSite.siteGuid !== pollSiteGuid) {
                scheduleNextPoll(SCAN_STATUS_POLL_MS);
                return;
            }

            const last = gql?.data?.getLastScan ?? gql?.data?.data?.getLastScan;
            if (last == null && !liveSite.addScanInFlight) {
                lastKnownDirectScanRef.current = null;
            }
            if (isServerDirectScanActive(last)) {
                lastKnownDirectScanRef.current = last;
            } else if (last && (isScanFlagSet(last.isComplete) || isScanFlagSet(last.isCanceled))) {
                lastKnownDirectScanRef.current = null;
            }

            const effectiveLast = isServerDirectScanActive(last)
                ? last
                : lastKnownDirectScanRef.current;
            const serverScanActive = isServerDirectScanActive(effectiveLast);

            const statusRows = parseDirectScanStatusRows(statusPayload);
            liveSite.directScanStatusRows = statusRows;
            if (effectiveLast?.id != null) {
                liveSite.directScanId = effectiveLast.id;
            }

            if (serverScanActive) {
                const fromBts = parseScanJsonCommand(effectiveLast.jsonCommand);
                if (fromBts.length > 0) {
                    liveSite.activeScanCableIds = fromBts;
                }
            }

            const activeIds = liveSite.activeScanCableIds;
            const scanCableIds = resolveActiveScanCableIds(activeIds, effectiveLast);
            const blinkVisibleIds = resolveBlinkVisibleCableIds(activeIds, selectedCablesRef.current, effectiveLast);

            if (!isScanFlagSet(effectiveLast?.isCanceled)) {
                syncFinishedCableSnapshots(statusRows, props.updateCables, scanCableIds, effectiveLast);
            }

            const scanActive = liveSite.addScanInFlight === true || serverScanActive;

            if (scanActive) {
                props.cableScanning(resolveBlinkCableId(effectiveLast, activeIds, statusRows, blinkVisibleIds));
                setIsScanning(true);
            } else {
                if (!liveSite.addScanInFlight) {
                    resetScanUiState();
                }
                props.cableScanning();
                setIsScanning(false);
            }

            scheduleNextPoll(SCAN_STATUS_POLL_MS);
        }).catch(function (error) {
            pollInFlightRef.current = false;
            if (isServerDirectScanActive(lastKnownDirectScanRef.current)) {
                setIsScanning(true);
            }
            scheduleNextPoll(SCAN_STATUS_POLL_MS);
            handleAuthRenew(error);
        });
    };

    checkIfScanningRef.current = checkIfScanning;



    const cancelScan = () => {

        const scanId = global.gw.selectedSite.directScanId ?? 0;

        let json = {

            id: scanId,

            dateFinished: new Date().toJSON(),

            isSuccessful: false,

            isComplete: true,

            isCanceled: true,

        };

        createApiEndpoint().fetchUpdateScan('direct', JSON.stringify(json)).then((gql) => {

            const result = gql?.data?.updateScan;

            if (result !== "canceled" && result !== "Saved.") {

                checkIfScanning();

                return;

            }

            resetScanUiState();

            props.cableScanning();

            setIsScanning(false);
            lastKnownDirectScanRef.current = null;

        }).catch(function () {

            checkIfScanning();

        });

    };



    return (

        <div

            style={{

                ...props.windowStyle,

                width: "auto",
                flexShrink: 0,
                minWidth: 0,

            }}

        >

            <div

                style={{

                    display: "flex",

                    alignItems: "center",

                    margin: 0,

                    width: "auto",
                    flexShrink: 0,
                    minWidth: 0,

                }}

            >

                <MuiMaterials.InputLabel

                    sx={{

                        margin: 0,

                        cursor: "pointer",

                        width: "auto",

                    }}

                >

                    <MuiMaterials.Button

                        key={`scan-button-${global.gw.theme}-${forceUpdate}`}

                        className="scanButton"

                        size="small"

                        variant="outlined"

                        sx={{
                            ...outlinedActionButtonSx(),
                            flexShrink: 0,
                            width: "auto",
                            cursor: 'pointer',
                            '& > *': {
                                pointerEvents: 'none',
                            },
                        }}

                        onClick={handleOpen}

                    >

                        {isScanning ? "Cancel" : "Start Scan"}

                    </MuiMaterials.Button>

                    <MuiMaterials.Modal

                        key={`scan-modal-${global.gw.theme}-${forceUpdate}`}

                        open={open}

                        onClose={handleClose}

                        aria-labelledby="modal-modal-title"

                        aria-describedby="modal-modal-description"

                        sx={confirmDialogBackdropSx()}
                    >
                        <MuiMaterials.Box sx={confirmDialogPaperSx()}>
                            <MuiMaterials.Typography
                                id="modal-modal-title"
                                variant="h6"
                                component="h2"
                                sx={confirmDialogTitleSx()}
                            >

                                {isScanning ? "Cancel the scan in progress?" : "Start a new scan?"}

                            </MuiMaterials.Typography>

                            <div style={confirmDialogActionsSx()}>

                                <MuiMaterials.Button

                                    sx={confirmDialogButtonSx()}
                                    size="small"
                                    variant="outlined"

                                    onClick={e => {

                                        handleClose();



                                        e.preventDefault = true;



                                        if (isScanning) {

                                            cancelScan();

                                        }

                                        else {

                                                const cableIds = (props.selectedCables || [])

                                                    .map((id) => Number(id))

                                                    .filter((id) => Number.isFinite(id));

                                                if (cableIds.length === 0) {

                                                    return;

                                                }



                                                global.gw.selectedSite.activeScanCableIds = cableIds;

                                                global.gw.selectedSite.finishedCables = [];

                                                global.gw.selectedSite.appliedScanSensorCables = [];

                                                global.gw.selectedSite.pendingScanSensorFetches = {};

                                                global.gw.selectedSite.addScanInFlight = true;



                                                let selectedCables = JSON.stringify(cableIds);

                                                clearTimeout(global.gw.selectedSite.refreshId);



                                                createApiEndpoint().fetchAddScan('direct', selectedCables).then((gql) => {

                                                    const saveResult = gql?.data?.addScan;
                                                    const graphQlErrors = gql?.errors?.map((e) => e?.message).filter(Boolean).join("; ");

                                                    if (!saveResult || !String(saveResult).startsWith("Saved")) {

                                                        const detail = saveResult || graphQlErrors || "addScan returned no response (check login / API).";

                                                        reportAddScanFailure(`Scan did not start: ${detail}`);

                                                        resetScanUiState();

                                                        props.cableScanning();

                                                        setIsScanning(false);

                                                        return;

                                                    }



                                                    setIsScanning(true);

                                                    scheduleNextPoll(SCAN_STATUS_POLL_MS);

                                                }).catch(function (error) {

                                                    reportAddScanFailure(`Scan did not start: ${error?.message || "network error"}`);

                                                    resetScanUiState();

                                                    props.cableScanning();

                                                    setIsScanning(false);

                                                }).finally(function () {

                                                    global.gw.selectedSite.addScanInFlight = false;

                                                    checkIfScanning();

                                                });

                                        }

                                    }}

                                >

                                    Yes

                                </MuiMaterials.Button>

                                <MuiMaterials.Button

                                    sx={confirmDialogButtonSx()}
                                    size="small"
                                    variant="outlined"

                                    onClick={handleClose}

                                >

                                    No

                                </MuiMaterials.Button>

                            </div>

                        </MuiMaterials.Box>

                    </MuiMaterials.Modal>

                </MuiMaterials.InputLabel>

            </div>

        </div>

    );

}

export default Scan;


